import React, { useState } from "react";
import { useI18n } from "@/i18n";
import { useReveal } from "@/hooks/useReveal";

function Item({ item, index, open, onToggle }) {
  return (
    <div
      data-testid={`faq-item-${index}`}
      className="border-b border-neutral-200 group"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`faq-trigger-${index}`}
        className="w-full text-left py-6 md:py-8 flex items-start justify-between gap-6"
      >
        <span className="font-display text-[18px] md:text-[22px] tracking-[-0.01em] text-neutral-950 font-medium pr-6">
          {item.q}
        </span>
        <span
          aria-hidden
          className={`mt-1 flex-none w-9 h-9 grid place-items-center rounded-full border transition-all duration-300 ${
            open
              ? "bg-neutral-950 text-white border-neutral-950 rotate-45"
              : "bg-white text-neutral-900 border-neutral-200 group-hover:border-[color:var(--wf-gold)]"
          }`}
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
      </button>
      <div
        data-testid={`faq-panel-${index}`}
        className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="pb-8 text-neutral-600 text-[15px] leading-relaxed max-w-[62ch]">
            {item.a}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const { t } = useI18n();
  const [openIdx, setOpenIdx] = useState(0);
  const header = useReveal();

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-36"
    >
      <div className="max-w-[1240px] mx-auto px-5 md:px-8">
        <div
          ref={header.ref}
          className={`reveal ${header.visible ? "is-visible" : ""} grid grid-cols-1 md:grid-cols-12 gap-10`}
        >
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 mb-5">
              <span
                className="inline-block w-6 h-px align-middle mr-3"
                style={{ background: "#c9a86a" }}
              />
              {t.faq.eyebrow}
            </div>
            <h2
              data-testid="faq-title"
              className="font-display text-[34px] sm:text-[44px] md:text-[52px] leading-[1.05] tracking-[-0.02em] font-light text-neutral-950 sticky top-28"
            >
              {t.faq.title}
            </h2>
          </div>

          <div className="md:col-span-7 border-t border-neutral-200">
            {t.faq.items.map((item, i) => (
              <Item
                key={i}
                item={item}
                index={i}
                open={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
